import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, Check, Loader2, PartyPopper, X } from 'lucide-react';
import { useJudgementAutosave } from '../rlhfShared/useJudgementAutosave';
import LabellingBar from './LabellingBar';
import { hasRequiredRemarks, isComplete, ResponseValue } from './InstrumentControls';
import { canRender, rendererFor } from './renderers';
import {
  Assignment, HTTP_CONFLICT, ItemPayload, LabellingApiError, StudyDetail,
  fetchItem, fetchNextAssignment, fetchQueue, fetchStudy, saveResponse,
} from './api';

/** Shown if a 409 ever arrives without the backend's own wording. */
const REASSIGNED_FALLBACK =
  'This item was reassigned after your claim expired. Move on to the next one.';

/**
 * `/label/<slug>` — one workbench for every labelling study (spec 37).
 *
 * Nothing here knows what a study is about. The instrument comes back as data and picks the
 * controls; each item's `render_kind` picks its renderer. That is what makes a new study a config
 * file rather than a pull request — and what let three near-identical pages collapse into this one.
 *
 * Autosave is the shared `useJudgementAutosave` hook, the same one /rate and /rate2 use, so the
 * rules that protect a rater's written reasoning live in exactly one place.
 */
const LabellingWorkbench: React.FC = () => {
  const { slug = '' } = useParams();

  const [study, setStudy] = useState<StudyDetail | null>(null);
  const [queue, setQueue] = useState<Assignment[]>([]);
  const [index, setIndex] = useState(0);
  // Tagged with the assignment they belong to. Without that, the frame between "current changed"
  // and "the load effect ran" shows the PREVIOUS unit's content as though it were this one's.
  const [items, setItems] = useState<{ assignmentId: number; payloads: ItemPayload[] } | null>(null);

  const [loading, setLoading] = useState(true);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [itemsError, setItemsError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);

  // Assignments the server has told us are no longer ours, by id, with its explanation. Terminal:
  // there is nothing to retry, so these are locked rather than left looking answerable.
  const [reassigned, setReassigned] = useState<Record<number, string>>({});
  // The same news, kept at page level so it survives being moved on to the next item — otherwise
  // the rater is relocated with no idea why.
  const [notice, setNotice] = useState<string | null>(null);

  // In-progress answers per assignment, held by the PAGE so moving between units never discards
  // work that has not reached the server yet.
  const [drafts, setDrafts] = useState<Record<number, { value: ResponseValue; remarks: string }>>({});

  // The status of the most recent failed save, so the navigation logic can tell a lost slot (409,
  // terminal) from a save worth retrying (everything else). `flush` only reports a boolean.
  const lastSaveStatusRef = useRef<number | null>(null);

  const current = queue[index] ?? null;
  const draft = current
    ? drafts[current.id] ?? {
      value: (current.response as ResponseValue) ?? {},
      remarks: current.remarks ?? '',
    }
    : { value: {}, remarks: '' };

  const currentReassigned = current ? Boolean(reassigned[current.id]) : false;
  /** Only ever the bodies that belong to the unit on screen. */
  const shown = items && current && items.assignmentId === current.id ? items : null;

  /**
   * Whether the rater is actually looking at what they are being asked to judge.
   *
   * The item panes and the scoring controls are independent regions, so an error pane and a live
   * scale used to coexist happily: a payload that failed to fetch, or a `render_kind` this build
   * has no renderer for, still left the buttons and Next enabled. A judgement recorded then is
   * indistinguishable in the export from one made against the real content.
   */
  const contentReady = Boolean(
    current
    && shown
    && !itemsLoading
    && !itemsError
    && shown.payloads.length === current.items.length
    && shown.payloads.every((item) => canRender(item.render_kind)),
  );

  /**
   * Whether there is an answer worth sending: the content on screen, the instrument satisfied, and
   * any required reason written. ONE definition, used by both the autosave gate and the Next
   * button — they disagreed before, which is how autosave came to fire answers Next would not.
   */
  const answerReady = Boolean(
    study
    && contentReady
    && isComplete(study.instrument, draft.value)
    && hasRequiredRemarks(study.instrument, draft.remarks),
  );

  const bootstrap = useCallback(async () => {
    setLoading(true);
    setFatalError(null);
    try {
      const detail = await fetchStudy(slug);
      setStudy(detail);
      let assignments = await fetchQueue(slug);
      if (assignments.length === 0) {
        const claimed = await fetchNextAssignment(slug);
        if (claimed) assignments = await fetchQueue(slug);
      }
      setQueue(assignments);
      // Land on the first thing still needing an answer rather than the top of the list.
      const firstUnanswered = assignments.findIndex((a) => !a.is_answered);
      setIndex(firstUnanswered === -1 ? Math.max(assignments.length - 1, 0) : firstUnanswered);
    } catch (e) {
      setFatalError(e instanceof Error ? e.message : 'Could not load this study');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { void bootstrap(); }, [bootstrap]);

  // Item bodies are fetched per assignment because a payload can be ~1 MB, so they are deliberately
  // absent from the queue.
  useEffect(() => {
    if (!current) return;
    const assignmentId = current.id;
    let cancelled = false;
    setItemsLoading(true);
    setItemsError(null);
    Promise.all(current.items.map((_, i) => fetchItem(assignmentId, i)))
      .then((loaded) => { if (!cancelled) setItems({ assignmentId, payloads: loaded }); })
      .catch((e) => {
        if (!cancelled) setItemsError(e instanceof Error ? e.message : 'Could not load this item');
      })
      .finally(() => { if (!cancelled) setItemsLoading(false); });
    return () => { cancelled = true; };
  }, [current?.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * Send the answer. Nothing else — the page's own copy of the queue is updated from `onSaved`,
   * which the hook calls only for the write that is actually the newest. Mutating it here instead
   * put page state outside that gate, so an older write landing last could walk the queue entry
   * backwards over a newer one.
   */
  const persist = useCallback(
    async (assignmentId: number, payload: { choice: ResponseValue; remarks: string }) => {
      try {
        await saveResponse(assignmentId, { response: payload.choice, remarks: payload.remarks });
        lastSaveStatusRef.current = null;
      } catch (e) {
        const status = e instanceof LabellingApiError ? e.status : null;
        lastSaveStatusRef.current = status;
        if (status === HTTP_CONFLICT) {
          // The slot went to another rater while this claim was lapsed. There is no version of
          // this answer the server will now accept, so the draft is dead: keeping it on screen
          // would show the rater a score that counts for nothing.
          const message = e instanceof Error && e.message ? e.message : REASSIGNED_FALLBACK;
          setReassigned((all) => ({ ...all, [assignmentId]: message }));
          setNotice(message);
          setDrafts((all) => {
            const rest = { ...all };
            delete rest[assignmentId];
            return rest;
          });
        }
        throw e;
      }
    },
    [],
  );

  /** The committed values, mirrored into the queue the form rehydrates from. */
  const mirrorToQueue = useCallback(
    (committed: { itemId: number; choice: ResponseValue; remarks: string }) => {
      setQueue((rows) => rows.map((row) => (
        row.id === committed.itemId
          ? { ...row, response: committed.choice, remarks: committed.remarks, is_answered: true }
          : row
      )));
    },
    [],
  );

  const { saveState, error, setBaseline, flush } = useJudgementAutosave<ResponseValue>({
    itemId: current?.id ?? null,
    choice: draft.value,
    remarks: draft.remarks,
    // Nothing goes to the server until the study's own rules say the answer is ready. The remarks
    // half matters as much as the score: without it the rater picks an answer, pauses to compose
    // their reason, and the debounce PUTs an empty one straight into a 422 — a red save error for
    // something they have not yet done wrong. And autosave writes a judgement into the dataset
    // without anyone pressing Next, so the content being on screen has to hold here too.
    enabled: answerReady,
    save: persist,
    onSaved: mirrorToQueue,
    failureMessage: 'Could not save your answer',
  });

  // Adopt the server's values as the baseline once per assignment, so merely opening one is never
  // mistaken for an edit. Keyed on assignment identity ONLY — depending on the answer too would
  // re-run after every save and wipe the rater's "Saved" confirmation.
  useEffect(() => {
    if (!current) return;
    setBaseline(current.id, (current.response as ResponseValue) ?? {}, current.remarks ?? '');
    // eslint-disable-line react-hooks/exhaustive-deps
  }, [current?.id, setBaseline]);  // eslint-disable-line react-hooks/exhaustive-deps

  const setDraft = useCallback((next: { value: ResponseValue; remarks: string }) => {
    if (!current) return;
    setDrafts((all) => ({ ...all, [current.id]: next }));
  }, [current]);

  const busy = saveState === 'saving';
  const isLast = index >= queue.length - 1;

  // A lost slot is the one case where moving on needs no answer — there is nothing left to give
  // one to, and requiring one is what turned "move on to the next one" into a locked door.
  const canAdvance = currentReassigned || answerReady;

  /** Move to the next unit, claiming more work when this was the last one. */
  const advance = useCallback(async () => {
    if (!isLast) { setIndex((i) => i + 1); return; }
    // Last in the queue: try to claim more before declaring the study done.
    const claimed = await fetchNextAssignment(slug).catch(() => null);
    if (claimed) {
      const refreshed = await fetchQueue(slug);
      setQueue(refreshed);
      const landing = refreshed.findIndex((a) => a.id === claimed.id);
      setIndex(landing === -1 ? Math.max(refreshed.length - 1, 0) : landing);
      return;
    }
    setFinished(true);
  }, [isLast, slug]);

  /**
   * Commit, then move — unless there is nothing left to commit to.
   *
   * A failed save blocks the move, because advancing would carry the rater away from work that was
   * never persisted. A 409 is the exception, and treating it like any other failure was a trap: the
   * server's own message tells the rater to move on to the next one while Next refuses, and every
   * press re-sends the same doomed write for the same 409, forever.
   */
  const commit = useCallback(async (asDraft: boolean) => {
    if (currentReassigned) return true;
    const saved = await flush(asDraft);
    return saved || lastSaveStatusRef.current === HTTP_CONFLICT;
  }, [currentReassigned, flush]);

  const goNext = useCallback(async () => {
    if (await commit(false)) await advance();
  }, [advance, commit]);

  // Moving backwards or sideways is not a declaration that the answer is finished, so it saves as a
  // draft: an untouched answer is left alone instead of being rewritten on every hop.
  const goPrev = useCallback(async () => {
    if (await commit(true)) setIndex((i) => Math.max(0, i - 1));
  }, [commit]);

  /**
   * Jump straight to another unit in the queue.
   *
   * Goes through the same commit as Prev/Next. Clicking a chip used to be a bare `setIndex`, and
   * because the autosave effect clears its armed timer when the item changes, anything typed inside
   * the 800ms debounce window was never sent by anyone — the one navigation control that silently
   * discarded the rater's work.
   */
  const goToIndex = useCallback(async (target: number) => {
    if (target === index) return;
    if (await commit(true)) setIndex(target);
  }, [commit, index]);

  const answeredCount = useMemo(() => queue.filter((a) => a.is_answered).length, [queue]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" aria-label="Loading study" />
      </div>
    );
  }

  if (fatalError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-3" aria-hidden="true" />
          <p className="text-sm text-gray-700 mb-4">{fatalError}</p>
          <button
            type="button"
            onClick={() => void bootstrap()}
            className="px-5 py-2.5 rounded-xl bg-gray-900 text-white text-sm font-semibold"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (finished || queue.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-6">
        <div className="max-w-md mx-auto text-center">
          <PartyPopper className="w-9 h-9 text-blue-600 mx-auto mb-3" aria-hidden="true" />
          <h1 className="text-lg font-semibold mb-1">Nothing left to rate</h1>
          <p className="text-gray-600 text-sm mb-6">
            {queue.length === 0
              ? 'There is nothing available in this study right now.'
              : `You’ve worked through ${study?.title ?? 'this study'} — ${answeredCount} of ${queue.length} answered. Thank you!`}
          </p>
          {queue.length > 0 && (
            <button
              type="button"
              onClick={() => { setFinished(false); setIndex(0); }}
              className="px-5 py-2.5 rounded-xl border-[1.5px] border-gray-200 bg-white text-sm font-semibold text-gray-700 hover:border-gray-900 hover:text-gray-900"
            >
              Review your answers
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto px-5 pt-7 max-w-[1140px]" style={{ paddingBottom: 240 }}>
        <header>
          <h1 className="text-xl font-bold tracking-tight text-gray-900 mb-1">{study?.title}</h1>
          {study?.instructions && (
            <p className="text-sm text-gray-600 max-w-[70ch]">{study.instructions}</p>
          )}
        </header>

        {notice && (
          <div
            role="status"
            className="flex items-start gap-3 mt-4 px-4 py-3 rounded-xl border border-amber-300 bg-amber-50"
          >
            <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" aria-hidden="true" />
            <p className="text-[13px] text-amber-900 flex-1">{notice}</p>
            <button
              type="button"
              onClick={() => setNotice(null)}
              aria-label="Dismiss"
              className="text-amber-700 hover:text-amber-900 shrink-0"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>
          </div>
        )}

        <div
          role="tablist"
          aria-label="Your queue"
          className="flex gap-2 mt-5 mb-3.5 overflow-x-auto pb-1"
        >
          {queue.map((assignment, position) => (
            <button
              key={assignment.id}
              type="button"
              role="tab"
              aria-selected={position === index}
              // Same rule as Prev/Next: no navigating out of a save that is still in flight.
              disabled={busy}
              onClick={() => void goToIndex(position)}
              className={`flex items-center gap-2 shrink-0 px-3.5 py-2 rounded-xl border text-[13px] font-semibold transition-all disabled:opacity-50 ${
                position === index
                  ? 'bg-gray-900 border-gray-900 text-white'
                  : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
              }`}
            >
              <span
                aria-hidden="true"
                className={`w-[18px] h-[18px] rounded-full grid place-items-center ${
                  assignment.is_answered
                    ? 'bg-blue-600 text-white'
                    : position === index ? 'bg-white/20' : 'bg-gray-100'
                }`}
              >
                {assignment.is_answered ? <Check className="w-3 h-3" /> : null}
              </span>
              {position + 1}
            </button>
          ))}
        </div>

        {itemsLoading && (
          <div className="h-[420px] grid place-items-center bg-white border border-gray-200 rounded-2xl">
            <Loader2 className="w-5 h-5 animate-spin text-blue-600" aria-label="Loading item" />
          </div>
        )}

        {!itemsLoading && itemsError && (
          <div className="p-10 text-center bg-white border border-gray-200 rounded-2xl">
            <AlertCircle className="w-7 h-7 text-red-500 mx-auto mb-3" aria-hidden="true" />
            <p className="text-sm text-gray-700">{itemsError}</p>
          </div>
        )}

        {currentReassigned && current && (
          <div className="mb-3.5 px-4 py-3 rounded-xl border border-gray-200 bg-gray-50">
            <p className="text-[13px] text-gray-700">{reassigned[current.id]}</p>
          </div>
        )}

        {/* One pane per item: a single item fills the width, a pair sits side by side. */}
        {!itemsLoading && !itemsError && shown && shown.payloads.length > 0 && (
          <div className={`grid gap-3.5 items-start ${shown.payloads.length > 1 ? 'lg:grid-cols-2' : ''}`}>
            {shown.payloads.map((item, position) => {
              const Renderer = rendererFor(item.render_kind);
              return (
                <section
                  key={item.id}
                  className="bg-white border border-gray-200 rounded-2xl overflow-hidden"
                >
                  <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200">
                    {shown.payloads.length > 1 && (
                      <span className="w-7 h-7 rounded-lg grid place-items-center text-[13px] font-bold bg-gray-100 text-gray-600">
                        {String.fromCharCode(65 + position)}
                      </span>
                    )}
                    <span className="text-sm font-semibold text-gray-900">
                      {item.title || item.item_ref}
                    </span>
                  </div>
                  <Renderer
                    item={item}
                    assignmentId={shown.assignmentId}
                    index={position}
                    stacked={shown.payloads.length > 1}
                  />
                </section>
              );
            })}
          </div>
        )}
      </div>

      {study && current && (
        <LabellingBar
          instrument={study.instrument}
          items={current.items}
          value={draft.value}
          onChange={(value) => setDraft({ ...draft, value })}
          remarks={draft.remarks}
          onRemarksChange={(remarks) => setDraft({ ...draft, remarks })}
          onPrev={() => void goPrev()}
          onNext={() => void goNext()}
          canGoPrev={index > 0}
          isLast={isLast}
          canAdvance={canAdvance}
          canAnswer={contentReady && !currentReassigned}
          busy={busy}
          saveState={saveState}
          error={error}
        />
      )}
    </div>
  );
};

export default LabellingWorkbench;
